/**
 * Places somebody has marked on the map of the world.
 *
 * Two kinds, and they are different things. A waypoint is where you are going:
 * there is one at a time, it is drawn on both maps with the distance to it, and
 * arriving is what takes it away. A marker is somewhere worth remembering: a
 * bank, a good tree, where the boat leaves from. There are as many as somebody
 * wants and they stay until they are deleted.
 *
 * Kept in the browser rather than on the character, because a mark is a note
 * about how one person plays rather than something the world has to agree with.
 * Kept per character, because two characters are two people.
 */

/** How many markers one character may keep, which is more than anybody needs. */
export const MARKER_MAX = 64;

/** What a marker may be drawn as. Words rather than pictures, so they are readable. */
export const MARKER_ICONS = ['dot', 'star', 'bank', 'skull', 'tree', 'anvil', 'home'] as const;

export type MarkerIcon = (typeof MARKER_ICONS)[number];

export const MARKER_COLOURS = [
  '#0cd5e3',
  '#f2e3c0',
  '#7ad17a',
  '#e6685c',
  '#c9a2ff',
  '#ffb865',
] as const;

export interface MapMarker {
  id: string;
  name: string;
  /** Where it is, in the world's own tiles, and which layer it is on. */
  tx: number;
  tz: number;
  layer: number;
  colour: string;
  icon: MarkerIcon;
}

/** Where somebody is headed, or nothing. */
export interface Waypoint {
  tx: number;
  tz: number;
  layer: number;
}

interface Marks {
  waypoint: Waypoint | null;
  markers: MapMarker[];
}

const EMPTY: Marks = { waypoint: null, markers: [] };

function keyFor(character: string): string {
  return `inverse.marks.${character.toLowerCase()}`;
}

export function loadMarks(character: string): Marks {
  if (!character) return EMPTY;

  try {
    const held = window.localStorage.getItem(keyFor(character));
    if (!held) return EMPTY;

    const parsed = JSON.parse(held) as Partial<Marks>;
    const markers = Array.isArray(parsed.markers) ? parsed.markers : [];

    return {
      waypoint: parsed.waypoint ?? null,
      markers: markers.filter(
        (one): one is MapMarker =>
          typeof one?.id === 'string' && typeof one.tx === 'number' && typeof one.tz === 'number',
      ),
    };
  } catch {
    // A note nobody can read is not worth a crash.
    return EMPTY;
  }
}

export function saveMarks(character: string, marks: Marks): void {
  if (!character) return;

  try {
    window.localStorage.setItem(
      keyFor(character),
      JSON.stringify({ waypoint: marks.waypoint, markers: marks.markers.slice(0, MARKER_MAX) }),
    );
  } catch {
    // Private browsing, a full quota. The marks last as long as the page does.
  }
}

/** A name for a new marker, which is where it is until somebody says otherwise. */
export function markerName(where: string, tx: number, tz: number): string {
  return where.length > 0 ? where : `${tx}, ${tz}`;
}
